import { useState } from 'react';
import { formatDate, formatEuro } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Select, Skeleton, Tooltip, useToast } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useReportToFinancier, useSelfDisclosureCompare, type FullView } from './api';
import styles from './SelfDisclosurePage.module.css';

export interface CompareTabProps {
  full: FullView;
}

const NO_FINANCIER = 'Kein Finanzierer zugewiesen.';

/** Selbstauskunft 09 „Versionsvergleich": the rows of the field catalog, changed ones highlighted, „Nur Änderungen", the volume card. */
export function CompareTab({ full }: CompareTabProps) {
  const { toast } = useToast();
  const [withId, setWithId] = useState<string | null>(null);
  const [onlyChanges, setOnlyChanges] = useState(false);
  const compare = useSelfDisclosureCompare(full.selfDisclosure.id, withId, true);
  const report = useReportToFinancier(full.selfDisclosure.id);
  const candidates = full.versions.filter(
    (version) => version.id !== full.selfDisclosure.id && version.submittedAt !== null,
  );

  if (compare.isPending) {
    return (
      <div className={styles.card} aria-busy="true">
        <Skeleton width="35%" height={20} />
        <div className={styles.skeletonRows}>
          <Skeleton width="80%" />
          <Skeleton width="70%" />
          <Skeleton width="75%" />
        </div>
      </div>
    );
  }
  if (compare.isError) {
    const error = compare.error;
    return (
      <Banner tone={error instanceof ApiError && error.status === 409 ? 'info' : 'danger'}>
        {errorMessage(error)}
      </Banner>
    );
  }

  const data = compare.data;
  const rows = onlyChanges ? data.rows.filter((row) => row.changed) : data.rows;
  const before = data.volume.before;
  const after = data.volume.after;
  const reportError = report.error ? errorMessage(report.error) : null;
  const reportLabel = data.financier ? `An ${data.financier.name} melden` : 'An Finanzierer melden';
  const reportButton = (
    <Button
      variant="secondary"
      size="small"
      disabled={data.financier === null}
      loading={report.isPending}
      onClick={() =>
        report.mutate(undefined, {
          onSuccess: (result) => toast({ message: `Änderungen an ${result.financier.name} gemeldet.` }),
        })
      }
    >
      {reportLabel}
    </Button>
  );

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardHeadText}>
            <div className={styles.cardTitle}>Versionsvergleich</div>
            {data.changedCount === 0 ? (
              <div className={styles.cardSubtitle}>
                Keine Änderung gegenüber Version {data.previous.version}
              </div>
            ) : null}
          </div>
          <div className={styles.cardHeadActions}>
            {candidates.length > 1 ? (
              <Select
                label="Vergleichen mit"
                labelHidden
                size="compact"
                value={withId ?? data.previous.id}
                options={candidates.map((version) => ({
                  value: version.id,
                  label: `V${version.version}${version.submittedAt ? ` · ${formatDate(version.submittedAt).slice(0, 6)}` : ''}`,
                }))}
                onChange={(event) => setWithId(event.target.value)}
              />
            ) : null}
            <button
              type="button"
              className={styles.textLink}
              aria-pressed={onlyChanges}
              onClick={() => setOnlyChanges((value) => !value)}
            >
              {onlyChanges ? 'Alle Angaben' : 'Nur Änderungen'}
            </button>
          </div>
        </div>
        <div className={styles.compareTable} role="table" aria-label="Versionsvergleich">
          <div className={styles.compareHead} role="row">
            <span role="columnheader">Angabe</span>
            <span role="columnheader">
              V{data.previous.version}
              {data.previous.submittedAt ? ` · ${formatDate(data.previous.submittedAt).slice(0, 6)}` : ''}
            </span>
            <span role="columnheader">
              V{data.version.version}
              {data.version.submittedAt ? ` · ${formatDate(data.version.submittedAt).slice(0, 6)}` : ''}
            </span>
          </div>
          <div className={styles.compareBody}>
            {rows.length === 0 ? (
              <p className={styles.cardNote}>Keine geänderten Angaben.</p>
            ) : (
              rows.map((row) => (
                <div
                  key={`${row.group}.${row.field}`}
                  role="row"
                  className={clsx(styles.compareRow, row.changed && styles.compareRowChanged)}
                >
                  <span role="cell" className={styles.compareLabel}>
                    {row.label}
                  </span>
                  <span
                    role="cell"
                    className={clsx(styles.compareOld, row.changed && styles.compareOldChanged)}
                  >
                    {row.oldValue}
                  </span>
                  <span
                    role="cell"
                    className={clsx(styles.compareNew, row.changed && styles.compareNewChanged)}
                  >
                    {row.newValue}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.volumeRecalc}>
          <div className={styles.volumeRecalcText}>
            <span className={styles.volumeRecalcTitle}>
              {before !== null && after !== null && before !== after ? 'Volumen neu berechnet' : 'Volumen'}
            </span>
            <span className={styles.volumeRecalcMeta}>
              {before !== null ? formatEuro(before) : '—'} → {after !== null ? formatEuro(after) : '—'}
              {full.volume
                ? ` · mit den Annahmen vom ${formatDate(full.volume.calculatedAt).slice(0, 6)}`
                : ''}
            </span>
            {reportError ? <span className={styles.fieldError}>{reportError}</span> : null}
          </div>
          {data.financier === null ? (
            <Tooltip content={NO_FINANCIER}>
              <span className={styles.disabledAnchor} tabIndex={0}>
                {reportButton}
              </span>
            </Tooltip>
          ) : (
            reportButton
          )}
        </div>
        {data.financier === null ? <p className={styles.cardNote}>{NO_FINANCIER}</p> : null}
      </div>
    </>
  );
}
